/**
 * Experiment run details — /experiments/:runId
 *
 * Cloudscape details-page pattern: resource ID as the title, global actions in
 * the header, a key-value summary, then the comparison table. Polls while the
 * run is active so a deep link to an in-progress run shows live progress.
 *
 * The comparison is the point of the page: production summary and experiment
 * summary side by side for the same ticket, from the same source snapshot.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Header from '@cloudscape-design/components/header';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import Link from '@cloudscape-design/components/link';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { useNavigate, useParams } from 'react-router-dom';
import {
  cancelExperiment,
  getExperiment,
  getExperimentResults,
  promoteExperiment,
} from '../api/experiments';
import { ExperimentConfig, ExperimentResultRow, ExperimentRun } from '../types/experiment';
import { Markdown } from '../components/Markdown';
import {
  ExperimentStatusBadge,
  POLL_INTERVAL_MS,
  absoluteTime,
  countsText,
  groupsText,
  isActive,
  progressText,
  resultsToCsv,
  scopeText,
  severitiesText,
} from '../components/experiments/experimentShared';

export function ExperimentRunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  const [run, setRun] = useState<ExperimentRun | null>(null);
  const [rows, setRows] = useState<ExperimentResultRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promoteResult, setPromoteResult] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const load = useCallback(() => {
    if (!runId) return;
    Promise.all([getExperiment(runId), getExperimentResults(runId).catch(() => null)])
      .then(([nextRun, nextResults]) => {
        if (!mountedRef.current) return;
        setRun(nextRun);
        if (nextResults) setRows(nextResults.results || []);
        setLoading(false);
        if (isActive(nextRun.status)) {
          pollRef.current = setTimeout(load, POLL_INTERVAL_MS);
        }
      })
      .catch((e: Error) => {
        if (!mountedRef.current) return;
        setLoading(false);
        setError(e.message);
      });
  }, [runId]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [load]);

  const handleCancel = async () => {
    if (!runId) return;
    try {
      await cancelExperiment(runId);
      if (pollRef.current) clearTimeout(pollRef.current);
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handlePromote = async () => {
    if (!runId) return;
    setPromoting(true);
    try {
      const res = await promoteExperiment(runId);
      setPromoteOpen(false);
      setPromoteResult(
        `${res.message} Config version ${res.version ?? '—'}` +
          (res.previousVersion ? ` (previous version ${res.previousVersion}).` : '.'),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPromoting(false);
    }
  };

  const COLUMNS = [
    {
      id: 'ticketId',
      header: 'Ticket',
      cell: (item: ExperimentResultRow) => (
        <Link
          href={`/tickets/${item.ticketId}`}
          onFollow={(e) => {
            e.preventDefault();
            navigate(`/tickets/${item.ticketId}`);
          }}
        >
          {item.ticketId}
        </Link>
      ),
    },
    { id: 'title', header: 'Title', cell: (item: ExperimentResultRow) => item.title || '—' },
    {
      id: 'production',
      header: 'Production summary',
      cell: (item: ExperimentResultRow) =>
        item.productionSummary ? (
          <Box variant="small">
            <Markdown content={item.productionSummary} flattenHeadings />
          </Box>
        ) : (
          <Box variant="small">
            <i>None on record</i>
          </Box>
        ),
    },
    {
      id: 'experiment',
      header: 'Experiment summary',
      cell: (item: ExperimentResultRow) =>
        item.error ? (
          <Box variant="small" color="text-status-error">
            {item.error}
          </Box>
        ) : item.experimentSummary ? (
          <Box variant="small">
            <Markdown content={item.experimentSummary} flattenHeadings />
          </Box>
        ) : (
          <Box variant="small">—</Box>
        ),
    },
    {
      id: 'latency',
      header: 'Latency',
      cell: (item: ExperimentResultRow) =>
        item.latencyMs != null ? `${(item.latencyMs / 1000).toFixed(1)}s` : '—',
    },
  ];

  if (loading && !run) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    );
  }

  if (!run) {
    return (
      <Alert type="error" header="Run not found">
        {error || `No experiment run ${runId}`}
      </Alert>
    );
  }

  const config: Partial<ExperimentConfig> = run.config || {};

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={run.name}
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              {isActive(run.status) && (
                <Button onClick={handleCancel}>Cancel run</Button>
              )}
              <Button disabled={!rows.length} onClick={() => resultsToCsv(run.runId, rows)}>
                Export CSV
              </Button>
              <Button
                variant="primary"
                disabled={run.status !== 'SUCCEEDED'}
                onClick={() => setPromoteOpen(true)}
              >
                Promote config
              </Button>
            </SpaceBetween>
          }
        >
          {run.runId.slice(0, 8)}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        {promoteResult && (
          <Alert
            type="success"
            header="Config promoted"
            dismissible
            onDismiss={() => setPromoteResult(null)}
          >
            {promoteResult}
          </Alert>
        )}
        {run.error && (
          <Alert type="error" header="Run failed">
            {run.error}
          </Alert>
        )}

        <Container header={<Header variant="h2">Run details</Header>}>
          <SpaceBetween size="m">
            <KeyValuePairs
              columns={4}
              items={[
                { label: 'Status', value: <ExperimentStatusBadge status={run.status} /> },
                { label: 'Progress', value: progressText(run) },
                { label: 'Model', value: config.model || '—' },
                { label: 'Results', value: countsText(run.result) },
                { label: 'Time window', value: scopeText(run.filters) },
                { label: 'Groups', value: groupsText(run.filters) },
                { label: 'Severities', value: severitiesText(run.filters) },
                { label: 'Ticket cap', value: String(run.ticketCap ?? '—') },
                { label: 'Started', value: absoluteTime(run.createdAt) },
                { label: 'Updated', value: absoluteTime(run.updatedAt) },
              ]}
            />
            <ExpandableSection headerText="Prompts under test">
              <SpaceBetween size="s">
                <Box>
                  <Box variant="awsui-key-label">System prompt</Box>
                  <Box variant="code">{config.systemPrompt || '(default)'}</Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">User prompt template</Box>
                  <Box variant="code">{config.userPrompt || '(default)'}</Box>
                </Box>
              </SpaceBetween>
            </ExpandableSection>
          </SpaceBetween>
        </Container>

        <Table
          columnDefinitions={COLUMNS}
          items={rows}
          variant="container"
          wrapLines
          header={
            <Header variant="h2" counter={rows.length ? `(${rows.length})` : undefined}>
              Production vs experiment
            </Header>
          }
          empty={
            <Box textAlign="center" padding={{ vertical: 'm' }}>
              <b>{isActive(run.status) ? 'Run in progress' : 'No results'}</b>
              <Box variant="p" color="text-status-inactive">
                {isActive(run.status)
                  ? 'Summaries appear here as tickets are processed.'
                  : 'This run produced no comparable tickets.'}
              </Box>
            </Box>
          }
        />
      </SpaceBetween>

      <Modal
        visible={promoteOpen}
        onDismiss={() => setPromoteOpen(false)}
        header="Promote this config to production?"
        footer={
          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal">
              <Button variant="link" onClick={() => setPromoteOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" loading={promoting} onClick={handlePromote}>
                Promote
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box>
            This replaces the production AI config with this run&apos;s model and prompts.
          </Box>
          {/*
            Be explicit about the limits of promotion. The config flip is
            reversible via SSM parameter versions, but a backfill is not, and no
            summary rollback exists — so the UI must not imply that promoting
            updates what is already on record.
          */}
          <Alert type="info" header="What this does and does not change">
            <ul>
              <li>
                Applies to <b>newly ingested tickets only</b>, within about five minutes.
              </li>
              <li>
                <b>Existing summaries are unchanged.</b> To apply it to tickets already ingested you
                must run a separate re-summarize, which <b>cannot be undone</b>.
              </li>
              <li>
                The config itself can be rolled back — every promotion creates a new parameter
                version.
              </li>
            </ul>
          </Alert>
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );
}
